import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  BondDefinitionCatalogOption,
  BondDefinitionDetail,
  BondDefinitionUpsertRequest,
  BondDocumentItem,
  BondFormatOption,
  BondFormulaItem,
  BondNoteItem,
  BondProceedSubtypeOption,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  NoteCreateRequest,
  NoteResponse,
  NoteUpdateRequest,
} from './bond-definition.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondDefinitionService {
  private readonly endpoint = apiUrl('Bond/definition');
  private readonly documentsEndpoint = apiUrl('document');
  private readonly notesEndpoint = apiUrl('Notes');

  constructor(private readonly http: HttpClient) {}

  getBondDefinition(securityId: string): Observable<BondDefinitionDetail> {
    return this.http.get<unknown>(`${this.endpoint}/${encodeURIComponent(securityId)}`).pipe(
      map((response) => this.normalizeBondDefinition(response)),
    );
  }

  saveBondDefinition(payload: BondDefinitionUpsertRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  createNote(payload: NoteCreateRequest): Observable<NoteResponse> {
    return this.http.post<unknown>(this.notesEndpoint, payload).pipe(
      map((response) => this.normalizeNote(response)),
    );
  }

  updateNote(id: number, payload: NoteUpdateRequest): Observable<void> {
    return this.http.put<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`, payload);
  }

  deleteNote(id: number): Observable<void> {
    return this.http.delete<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`);
  }

  getDocumentsBySecurity(securityId: string): Observable<BondDocumentItem[]> {
    return this.http.get<unknown>(`${this.documentsEndpoint}/security/${encodeURIComponent(securityId)}`).pipe(
      map((response) => this.toDocuments(response)),
    );
  }

  createDocument(payload: DocumentCreateRequest): Observable<BondDocumentItem> {
    const formData = new FormData();
    formData.append('Filename', payload.filename);
    if (payload.parentElementId) {
      formData.append('ParentElementId', payload.parentElementId);
    }
    if (payload.securityId) {
      formData.append('SecurityId', payload.securityId);
    }
    formData.append('Description', payload.description ?? '');
    formData.append('Type', payload.type);
    formData.append('Source', payload.source);
    formData.append('ModifiedBy', payload.modifiedBy);
    formData.append('File', payload.file);

    return this.http.post<unknown>(this.documentsEndpoint, formData).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  updateDocument(id: number, payload: DocumentUpdateRequest): Observable<BondDocumentItem> {
    return this.http.put<unknown>(
      `${this.documentsEndpoint}/${encodeURIComponent(String(id))}`,
      payload,
    ).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`);
  }

  private normalizeBondDefinition(response: unknown): BondDefinitionDetail {
    const record = this.extractResponseRecord(response);

    return {
      dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
      structureId: this.toText(this.read(record, ['structureId', 'StructureId'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      secId: this.toText(this.read(record, ['secId', 'SecId', 'securityId', 'SecurityId'])),
      cusip: this.toText(this.read(record, ['cusip', 'Cusip', 'CUSIP'])),
      isin: this.toText(this.read(record, ['isin', 'Isin', 'ISIN'])),
      ccy: this.toText(this.read(record, ['ccy', 'Ccy', 'currency', 'Currency'])),
      coupon: this.toNullableNumber(this.read(record, ['coupon', 'Coupon'])),
      settlementDate: this.toText(this.read(record, ['settlementDate', 'SettlementDate'])),
      maturityDate: this.toText(this.read(record, ['maturityDate', 'MaturityDate'])),
      tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
      securityType: this.toText(this.read(record, ['securityType', 'SecurityType'])),
      marketType: this.toText(this.read(record, ['marketType', 'MarketType'])),
      notional: this.toNullableNumber(this.read(record, ['notional', 'Notional'])),
      tmsUser: this.toText(this.read(record, ['tmsUser', 'TmsUser', 'tms_User', 'Tms_User', 'TMS_User'])),
      isSale: this.toBoolean(this.read(record, ['isSale', 'IsSale'])),
      isNew: this.toBoolean(this.read(record, ['isNew', 'IsNew'])),
      modifiedBy: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy'])),
      dateModified: this.toText(this.read(record, ['dateModified', 'DateModified'])),
      comments: this.toText(this.read(record, ['comments', 'Comments'])),
      genericField1: this.toNullableNumber(this.read(record, ['genericField1', 'GenericField1'])),
      genericField2: this.toTextLike(this.read(record, ['genericField2', 'GenericField2'])),
      genericField3: this.toText(this.read(record, ['genericField3', 'GenericField3'])),
      bondDocumentationId: this.toNullableNumber(this.read(record, ['bondDocumentationId', 'BondDocumentationId'])),
      bondProceedId: this.toNullableNumber(this.read(record, ['bondProceedId', 'BondProceedId'])),
      bondProceedSubtypeId: this.toNullableNumber(this.read(record, ['bondProceedSubtypeId', 'BondProceedSubtypeId'])),
      bondFormatId: this.toNullableNumber(this.read(record, ['bondFormatId', 'BondFormatId', 'bondTypeId', 'BondTypeId'])),
      bondListingId: this.toNullableNumber(this.read(record, ['bondListingId', 'BondListingId'])),
      bondPricingSupplement: this.toText(this.read(record, ['bond_Pricing_Supplement', 'Bond_Pricing_Supplement'])),
      bondDocumentations: this.toCatalogOptions(this.read(record, ['bondDocumentations', 'BondDocumentations'])),
      bondProceeds: this.toCatalogOptions(this.read(record, ['bondProceeds', 'BondProceeds'])),
      bondProceedsSubtype: this.toProceedSubtypes(this.read(record, ['bondProceedsSubtype', 'BondProceedsSubtype', 'bondProceedSubtypes', 'BondProceedSubtypes'])),
      bondFormatTypes: this.toFormatOptions(this.read(record, ['bondFormatTypes', 'BondFormatTypes', 'bondFormats', 'BondFormats'])),
      bondListings: this.toCatalogOptions(this.read(record, ['bondListings', 'BondListings'])),
      documentsAttached: this.toDocuments(this.read(record, ['documentsAttached', 'DocumentsAttached'])),
      notes: this.toNotes(this.read(record, ['notes', 'Notes'])),
      formulas: this.toFormulas(this.read(record, ['formulas', 'Formulas'])),
    };
  }

  private normalizeNote(response: unknown): NoteResponse {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);
    const source = Object.keys(data).length > 0 ? data : record;

    return {
      id: this.toNumber(this.read(source, ['id', 'Id'])),
      parentId: this.toText(this.read(source, ['parentId', 'ParentId'])),
      source: this.toText(this.read(source, ['source', 'Source'])),
      description: this.toText(this.read(source, ['description', 'Description'])),
      modifiedBy: this.toText(this.read(source, ['modifiedBy', 'ModifiedBy'])),
      dateModified: this.toText(this.read(source, ['dateModified', 'DateModified'])),
    };
  }

  private extractResponseRecord(response: unknown): Record<string, unknown> {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);

    return Object.keys(data).length > 0 ? data : record;
  }

  private toCatalogOptions(value: unknown): BondDefinitionCatalogOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id', 'documentationId', 'DocumentationId', 'proceedId', 'ProceedId'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
      };
    });
  }

  private toProceedSubtypes(value: unknown): BondProceedSubtypeOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        proceedId: this.toNumber(this.read(record, ['proceedId', 'ProceedId'])),
        proceedName: this.toText(this.read(record, ['proceedName', 'ProceedName'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
      };
    });
  }

  private toFormatOptions(value: unknown): BondFormatOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        format: this.toText(this.read(record, ['format', 'Format', 'name', 'Name'])),
      };
    });
  }

  private toDocuments(value: unknown): BondDocumentItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => this.toDocument(this.toRecord(entry)));
  }

  private toDocument(record: Record<string, unknown>): BondDocumentItem {
    return {
      id: this.toNumber(this.read(record, ['id', 'Id', 'documentId', 'DocumentId'])),
      description: this.toText(this.read(record, ['description', 'Description', 'name', 'Name', 'documentName', 'DocumentName'])),
      type: this.toText(this.read(record, ['type', 'Type', 'documentType', 'DocumentType'])),
      filename: this.toText(this.read(record, ['filename', 'Filename', 'fileName', 'FileName'])),
      ezShareId: this.toText(this.read(record, ['ezShareId', 'EzShareId', 'eZShareId', 'EZShareId'])),
      ezShareLink: this.toText(this.read(record, ['ezShareLink', 'EzShareLink', 'eZShareLink', 'EZShareLink'])),
    };
  }

  private toNotes(value: unknown): BondNoteItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id', 'noteId', 'NoteId'])),
        note: this.toText(this.read(record, ['description', 'Description', 'note', 'Note'])),
        author: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy', 'author', 'Author'])),
        createdAt: this.toText(this.read(record, ['dateModified', 'DateModified', 'createdAt', 'CreatedAt'])),
      };
    });
  }

  private toFormulas(value: unknown): BondFormulaItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        securityId: this.toText(this.read(record, ['securityId', 'SecurityId'])),
        assetId: this.toText(this.read(record, ['assetId', 'AssetId'])),
        applyDate: this.toText(this.read(record, ['applyDate', 'ApplyDate'])),
        formula: this.toText(this.read(record, ['formula', 'Formula'])),
      };
    });
  }

  private read(record: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (key in record) {
        return record[key];
      }
    }

    return undefined;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toTextLike(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return '';
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = this.toNumber(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }
}
